import React from "react";
import { Sparkles, BookOpen, Loader2, AlertCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import "katex/dist/katex.min.css";
import styles from "../styles/highlightExplainerPanel.module.css";

/**
 * HighlightExplainerPanel
 * -----------------------
 * Companion to PdfHighlightReader. Shows a stack of explanation "cards",
 * newest first. Each card renders:
 *   • the highlighted passage (collapsible)
 *   • an explanation paragraph (markdown + LaTeX)
 *   • key points as bullets
 *   • jargon glossary (term → definition)
 *   • source chip ("doc.pdf · p. 14")
 *
 * Props:
 *   - items: array of
 *       { status: 'loading' | 'done' | 'error',
 *         meta: { doc_name, page, selected_text, mode },
 *         result?: { explanation, key_points, jargon, source },
 *         error?: string }
 *   - onJumpToSource?(source): optional; host can open the PDF to that page.
 */
const modeLabel = {
  explain: "Explain",
  summarize: "Summarize",
  connect: "Connect",
  simpler: "ELI5",
};

const Card = ({ item, onJumpToSource }) => {
  const { status, meta, result, error } = item;
  const [showSelection, setShowSelection] = React.useState(false);

  const source = result?.source || meta || {};
  const docName = source.doc_name || "Document";
  const page = source.page;
  const selectedText = source.selected_text || meta?.selected_text || "";

  return (
    <div className={styles.card} data-status={status}>
      <div className={styles.cardHeader}>
        <span className={styles.modeBadge}>
          <Sparkles size={10} />
          {modeLabel[meta?.mode] || "Explain"}
        </span>
        <button
          type="button"
          className={styles.sourceChip}
          onClick={() => onJumpToSource && onJumpToSource(source)}
          title={
            onJumpToSource
              ? "Jump to this page in the reader"
              : `Sourced from ${docName}`
          }
        >
          <BookOpen size={11} />
          <span className={styles.sourceChipText}>
            {docName}
            {page ? ` · p. ${page}` : ""}
          </span>
        </button>
      </div>

      {selectedText && (
        <div className={styles.selectionBlock}>
          <button
            type="button"
            className={styles.selectionToggle}
            onClick={() => setShowSelection((s) => !s)}
          >
            {showSelection ? "Hide selection" : "Show selection"}
          </button>
          {showSelection && (
            <blockquote className={styles.selectionQuote}>
              “{selectedText}”
            </blockquote>
          )}
        </div>
      )}

      {status === "loading" && (
        <div className={styles.loadingBlock}>
          <Loader2 size={14} className={styles.spin} />
          <span>Reading the passage…</span>
        </div>
      )}

      {status === "error" && (
        <div className={styles.errorBlock}>
          <AlertCircle size={14} />
          <span>{error || "Something went wrong."}</span>
        </div>
      )}

      {status === "done" && result && (
        <>
          {result.explanation && (
            <div className={styles.explanation}>
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
              >
                {result.explanation}
              </ReactMarkdown>
            </div>
          )}

          {Array.isArray(result.key_points) && result.key_points.length > 0 && (
            <div className={styles.keyPointsBlock}>
              <div className={styles.sectionTitle}>Key points</div>
              <ul className={styles.keyPointsList}>
                {result.key_points.map((kp, i) => (
                  <li key={i} className={styles.keyPointsItem}>
                    <span className={styles.bullet}>•</span>
                    <span>{kp}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(result.jargon) && result.jargon.length > 0 && (
            <div className={styles.jargonBlock}>
              <div className={styles.sectionTitle}>Glossary</div>
              <dl className={styles.jargonList}>
                {result.jargon.map((j, i) => (
                  <React.Fragment key={i}>
                    <dt className={styles.jargonTerm}>{j.term}</dt>
                    <dd className={styles.jargonDef}>{j.definition}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const HighlightExplainerPanel = ({ items = [], onJumpToSource }) => {
  if (!items || items.length === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyIcon}>◇</span>
        <div className={styles.emptyTitle}>Highlight any text to ask…</div>
        <div className={styles.emptyHint}>
          Open a PDF in the workspace, drag-select a passage, and Lumi will
          explain it here — grounded in the page content.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panelRoot}>
      {items.map((item, i) => (
        <Card
          key={item.id ?? i}
          item={item}
          onJumpToSource={onJumpToSource}
        />
      ))}
    </div>
  );
};

export default HighlightExplainerPanel;
